import type { CSSProperties } from "react";
import { CMARKDOWNVISUAL_MODE, type CmarkdownvisualMode } from "./CmarkdownvisualModeContext";
import { getFontFamily } from "@/theme/typography";

export const SCROLL_DIRECTION_VERTICAL = "vertical";

// `.w` canvas surface from planning/UI-refs/Interactive Widgets _standalone_.html:
// mint glow at top-right + dark linear darken. Lets the parent surface show through.
export const EXPANDED_CANVAS_BACKGROUND = [
  "radial-gradient(ellipse 500px 250px at 100% 0%, rgba(102, 254, 193, 0.05), transparent 60%)",
  "linear-gradient(180deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.55))",
].join(", ");

// `.w` shell - radius and shadow, overriding the shared markdownBlock defaults
// for the expanded canvas only.
export const EXPANDED_CANVAS_RADIUS = 14;
export const EXPANDED_CANVAS_SHADOW =
  "inset 0 0 0 1px rgba(255,255,255,0.08), 0 20px 50px -20px rgba(0,0,0,0.6)";
export const EXPANDED_CANVAS_BACKDROP_BLUR = "blur(20px) saturate(140%)";

enum CmarkdownvisualCanvasChromeState {
  Concaved = "concaved",
  Raised = "raised",
}

enum CmarkdownvisualCanvasVisibilityState {
  Hidden = "hidden",
  Visible = "visible",
}

type CmarkdownvisualCanvasChromeStyle = Pick<CSSProperties, "borderRadius" | "boxShadow">;

const CANVAS_CHROME_STYLE_BY_STATE: Record<
  CmarkdownvisualCanvasChromeState,
  CmarkdownvisualCanvasChromeStyle
> = {
  [CmarkdownvisualCanvasChromeState.Concaved]: {
    borderRadius: 0,
    boxShadow: "none",
  },
  [CmarkdownvisualCanvasChromeState.Raised]: {
    borderRadius: EXPANDED_CANVAS_RADIUS,
    boxShadow: EXPANDED_CANVAS_SHADOW,
  },
};

const CANVAS_DISPLAY_BY_STATE: Record<
  CmarkdownvisualCanvasVisibilityState,
  CSSProperties["display"] | undefined
> = {
  [CmarkdownvisualCanvasVisibilityState.Hidden]: "none",
  [CmarkdownvisualCanvasVisibilityState.Visible]: undefined,
};

interface CmarkdownvisualCanvasChromeInput {
  concaved: boolean;
}

interface CmarkdownvisualCanvasDisplayInput {
  isModalOpen: boolean;
}

function getCmarkdownvisualCanvasChromeState({
  concaved,
}: CmarkdownvisualCanvasChromeInput): CmarkdownvisualCanvasChromeState {
  if (concaved) {
    return CmarkdownvisualCanvasChromeState.Concaved;
  }

  return CmarkdownvisualCanvasChromeState.Raised;
}

function getCmarkdownvisualCanvasVisibilityState({
  isModalOpen,
}: CmarkdownvisualCanvasDisplayInput): CmarkdownvisualCanvasVisibilityState {
  if (isModalOpen) {
    return CmarkdownvisualCanvasVisibilityState.Hidden;
  }

  return CmarkdownvisualCanvasVisibilityState.Visible;
}

export function getExpandedCanvasChromeStyle(
  input: CmarkdownvisualCanvasChromeInput,
): CmarkdownvisualCanvasChromeStyle {
  return CANVAS_CHROME_STYLE_BY_STATE[getCmarkdownvisualCanvasChromeState(input)];
}

export function getExpandedCanvasDisplay({
  isModalOpen,
}: CmarkdownvisualCanvasDisplayInput): CSSProperties["display"] | undefined {
  return CANVAS_DISPLAY_BY_STATE[
    getCmarkdownvisualCanvasVisibilityState({ isModalOpen })
  ];
}

// `.w.is-expanded .w-body` - content min-height. Padding is left to callers
// via `contentStyle` so widgets that fill edge-to-edge (gantt, tables) aren't
// inset by default.
export const EXPANDED_BODY_MIN_HEIGHT = 340;
// Cap the expanded canvas height to 60% of the viewport. Content past this
// scrolls inside the Cscrollable wrapper.
export const EXPANDED_CANVAS_MAX_HEIGHT = "60vh";

export const ACCENT = "#66FEC1";

// `.w-bar` - semi-transparent dark bar over the canvas gradient.
export const TOP_BAR_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.05)",
  backgroundColor: "rgba(0,0,0,0.25)",
  gap: 8,
  flexShrink: 0,
};

// `.w-bar .left` / `.w-bar .right`
export const TOP_BAR_GROUP_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  minWidth: 0,
};

// `.w-bar .dot` - 6x6 mint with strong glow.
export const TOP_BAR_DOT_STYLE: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: ACCENT,
  boxShadow: `0 0 10px ${ACCENT}`,
  margin: "0 6px 0 4px",
  flexShrink: 0,
};

// `.w-bar .kind` - the type label as a mint-bordered pill. Sized at 1.25x the
// reference (9px->11px font, 3/7px->4/9px padding, 4->5 radius) per request.
export const TYPE_PILL_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 11,
  padding: "4px 9px",
  borderRadius: 5,
  boxShadow: "inset 0 0 0 1px rgba(102,254,193,0.3)",
  color: ACCENT,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

// `.w-bar .title` - optional per-visual label rendered next to the type pill.
// Only present when a caller passes `title`; the shell renders nothing when
// absent, so this never reserves layout space on its own.
export const VISUAL_TITLE_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 13,
  fontWeight: 600,
  color: "rgba(255,255,255,0.78)",
  letterSpacing: "-0.01em",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

// `.w-bar .sep` - vertical separator between icon groups on the right.
export const TOP_BAR_SEPARATOR_STYLE: CSSProperties = {
  width: 1,
  height: 14,
  backgroundColor: "rgba(255,255,255,0.08)",
  margin: "0 3px",
  flexShrink: 0,
  display: "inline-block",
};

export const CONTENT_BASE_STYLE: CSSProperties = {
  position: "relative",
  overflow: "hidden",
};

// Failure surface. A drawn visual fills `EXPANDED_BODY_MIN_HEIGHT`; a message
// does not, so the error body overrides it to a band that reads as an
// intentional state rather than a gutted canvas.
const ERROR_CONTENT_MIN_HEIGHT = 148;
export const ERROR_HEADLINE_SIZE = 16;
export const ERROR_DETAIL_SIZE = 14;
export const ERROR_DETAIL_OPACITY = 0.62;

export const ERROR_CONTENT_STYLE: CSSProperties = {
  minHeight: ERROR_CONTENT_MIN_HEIGHT,
  flex: 1,
};

export const ERROR_BODY_STYLE: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  paddingTop: 28,
  paddingBottom: 28,
  paddingLeft: 24,
  paddingRight: 24,
  textAlign: "center",
};

// A dimmed sibling of the top-bar dot: same vocabulary as a live visual, with
// the mint glow dropped so the state reads as inert rather than alarming.
export const ERROR_MARK_STYLE: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.22)",
  marginBottom: 6,
  flexShrink: 0,
};

// Top-bar icon buttons use Cbutton with the Ghost preset (transparent bg, no
// border, white-tinted hover). 24x24 to match the reference `.w-bar button`.
export const ICON_BTN_SIZE = 32;
export const ICON_BTN_ICON_SIZE = 20;
export const ICON_BTN_RADIUS = 6;
export const ICON_BTN_CONTAINER_STYLE: CSSProperties = {
  width: ICON_BTN_SIZE,
  minWidth: ICON_BTN_SIZE,
};

// Compact widget styles
//
// WIDGET_WRAP_STYLE: the centering container returned as the block root in compact mode.
// Deliberately has no explicit `width` - its width comes from the parent flex column's
// align-items:stretch. This avoids the circular-sizing issue that `width:100%` causes
// inside inline-flex containers (e.g. chat bubbles). justify-content:center then
// horizontally centers the fixed-width widget inside the stretched wrapper.
export const WIDGET_WRAP_STYLE: CSSProperties = {
  display: "flex",
  justifyContent: "center",
};

export const WIDGET_STYLE: CSSProperties = {
  width: 317,
  height: 317,
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  backgroundImage: "radial-gradient(ellipse 248px 138px at 100% 0%, rgba(102,254,193,0.07), transparent 70%), linear-gradient(180deg, rgba(0,0,0,0.42), rgba(0,0,0,0.62))",
  flexShrink: 0,
  userSelect: "none",
  overflow: "hidden",
};

export const WIDGET_HEAD_STYLE: CSSProperties = {
  padding: "14px 15px 6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
};

export const WIDGET_TAG_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontFamily: getFontFamily(),
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.72)",
};

export const WIDGET_DOT_STYLE: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: "#66FEC1",
  boxShadow: "0 0 8px #66FEC1",
  flexShrink: 0,
};

export const WIDGET_EXPAND_BTN_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: 7,
  border: "1px solid rgba(255,255,255,0.09)",
  backgroundColor: "rgba(255,255,255,0.04)",
  cursor: "pointer",
  color: "rgba(255,255,255,0.35)",
  flexShrink: 0,
  padding: 0,
  outline: "none",
};

export const WIDGET_BODY_STYLE: CSSProperties = {
  flex: 1,
  position: "relative",
  overflow: "hidden",
  maskImage: "linear-gradient(0deg, black 85%, transparent 100%)",
};

export const GRID_OVERLAY_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  backgroundImage:
    "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
  backgroundSize: "28px 28px",
  maskImage:
    "radial-gradient(ellipse 221px 166px at 50% 40%, rgba(0,0,0,0.6) 0%, transparent 100%)",
  WebkitMaskImage:
    "radial-gradient(ellipse 221px 166px at 50% 40%, rgba(0,0,0,0.6) 0%, transparent 100%)",
  pointerEvents: "none",
};

export const WIDGET_METRIC_STYLE: CSSProperties = {
  position: "absolute",
  left: 16,
  bottom: 12,
  pointerEvents: "none",
  // Above compact-preview vignettes so "N PLACES" never fades with the content.
  zIndex: 5,
};

export const WIDGET_METRIC_NUM_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 30,
  fontWeight: 800,
  letterSpacing: "-0.02em",
  color: "rgba(255,255,255,0.88)",
  lineHeight: 1,
};

export const WIDGET_METRIC_UNIT_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
  fontSize: 13,
  color: "rgba(255,255,255,0.5)",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginTop: 2,
};

export enum CmarkdownvisualHoverState {
  Idle = "idle",
  Hovered = "hovered",
}

export const WIDGET_SHADOW_BY_HOVER_STATE: Record<CmarkdownvisualHoverState, string> = {
  [CmarkdownvisualHoverState.Idle]:
    "inset 0 0 0 1px rgba(255,255,255,0.08), 0 20px 50px -20px rgba(0,0,0,0.6)",
  [CmarkdownvisualHoverState.Hovered]:
    "inset 0 0 0 1px rgba(102,254,193,0.22), 0 20px 50px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(102,254,193,0.06)",
};

export const SPRING_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const PORTAL_KEYBOARD_WRAPPER_BASE_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  flex: "1 1 auto",
  width: "100%",
  height: "100%",
  minHeight: 0,
  position: "relative",
  outline: "none",
};

const PORTAL_KEYBOARD_WRAPPER_MODAL_STYLE: CSSProperties = {
  ...PORTAL_KEYBOARD_WRAPPER_BASE_STYLE,
  borderRadius: "2rem",
  overflow: "hidden",
  boxShadow: "inset 0 2px 6px rgba(0,0,0,0.30)",
};

export const PORTAL_KEYBOARD_WRAPPER_STYLE_BY_MODE: Record<
  CmarkdownvisualMode,
  CSSProperties
> = {
  [CMARKDOWNVISUAL_MODE.Compact]: PORTAL_KEYBOARD_WRAPPER_BASE_STYLE,
  [CMARKDOWNVISUAL_MODE.Canvas]: PORTAL_KEYBOARD_WRAPPER_BASE_STYLE,
  [CMARKDOWNVISUAL_MODE.Modal]: PORTAL_KEYBOARD_WRAPPER_MODAL_STYLE,
};
